import React, { useEffect, useState } from "react";
import axios from "axios";
import { Sparkles } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { usePlayer } from "@/context/PlayerContext";
import { Header } from "@/components/Header";
import { PlayerBar } from "@/components/PlayerBar";
import { SongRow } from "@/components/SongRow";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// A reusable themed room that shows songs carrying a given tag.
export const TagRoom = ({ titleKey, subKey, heroImg, tag, testid }) => {
  const { t, fontClass } = useLang();
  const { current } = usePlayer();
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    axios.get(`${API}/songs`)
      .then((r) => { if (live) setSongs(r.data.filter((s) => (s.tags || []).includes(tag))); })
      .catch(() => {}).finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [tag]);

  return (
    <div className="min-h-screen pb-32" style={{ background: "var(--bg)" }} data-testid={`${testid}-room`}>
      <Header />

      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroImg} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, color-mix(in srgb, var(--bg) 45%, transparent) 0%, color-mix(in srgb, var(--bg) 80%, transparent) 55%, var(--bg) 100%)" }} />
        </div>
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-14 pb-12 sm:pt-20 sm:pb-16">
          <div className="max-w-2xl sk-fade-up">
            <span className="inline-flex items-center gap-2 font-ui text-xs sm:text-sm font-bold tracking-widest uppercase px-3 py-1.5 rounded-full mb-5" style={{ background: "var(--brass-light)", color: "var(--accent)" }}>
              <Sparkles size={16} /> {t("quiet_note")}
            </span>
            <h1 className={`${fontClass()} text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight`} style={{ color: "var(--text)" }}>
              {t(titleKey)}
            </h1>
            <p className="font-ui text-base sm:text-lg leading-relaxed mt-4" style={{ color: "var(--text-2)" }}>
              {t(subKey)}
            </p>
          </div>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 pt-8">
        {loading ? (
          <div className="py-16 text-center font-ui text-lg" style={{ color: "var(--text-muted)" }}>{t("loading")}</div>
        ) : songs.length === 0 ? (
          <div className="py-16 text-center font-ui text-lg" style={{ color: "var(--text-muted)" }}>{t("no_songs")}</div>
        ) : (
          <div className="space-y-3">
            {songs.map((song, i) => (
              <SongRow key={song.id} song={song} index={i} list={songs} />
            ))}
          </div>
        )}
      </section>

      {current && <div style={{ height: 8 }} />}
      <PlayerBar />
    </div>
  );
};
